import { AnalyticEventsClient } from "@timeedit/registration-schemas";
import { isValidDate, MinMaxDates } from "@timeedit/registration-shared";
import { Segmented, Typography } from "@timeedit/ui-components";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { ToolbarProps as RBCToolbarProps } from "react-big-calendar";
import { CalendarEvent } from "../../../../utils/events";
import { RangeReduce } from "../../utils";
import { RegistrationRequiredTranslations, TAnalyticsEvent } from "../../types";
import UnorderedListOutlined from "@timeedit/ui-components/icons/UnorderedListOutlined";
import CalendarOutlined from "@timeedit/ui-components/icons/CalendarOutlined";

export enum DisplayState {
  Graphical = "Graphical",
  Text = "Text",
}

type CalendarToolbarProps = RBCToolbarProps<CalendarEvent> & {
  setDisplayState: Dispatch<SetStateAction<DisplayState>>;
  displayState: DisplayState;
  dateInterval: MinMaxDates;
  translations: RegistrationRequiredTranslations;
  analyticsEvent?: TAnalyticsEvent;
};

/**
 * Toolbar above the registration calendar: the list/calendar switch and the date range of the shown groups.
 * @param displayState - Current view
 * @param setDisplayState - Called with the view the student picks
 * @param dateInterval - First and last date of the shown groups
 * @param translations - Labels for the switch options
 * @param analyticsEvent - Optional tracking of switch presses
 */
export function CalendarToolbar(
  props: CalendarToolbarProps
): React.JSX.Element {
  const {
    setDisplayState,
    displayState,
    dateInterval,
    translations,
    analyticsEvent,
  } = props;

  const dateRangeDays = new Set([
    dayjs(dateInterval.min.local).startOf("day").unix(),
    dayjs(dateInterval.max.local).startOf("day").unix(),
  ]);

  return (
    <div className="rbc-toolbar">
      <Segmented
        onChange={(value) => {
          const newDisplayState = value as DisplayState;
          if (analyticsEvent) {
            analyticsEvent(AnalyticEventsClient.buttonClick, {
              context: "CalendarToolbar",
              button_name: newDisplayState,
            });
          }

          setDisplayState(newDisplayState);
        }}
        defaultValue={displayState}
        options={[
          {
            label: translations.textview,
            value: DisplayState.Text,
            icon: <UnorderedListOutlined alt="Text List" />,
          },
          {
            label: translations.graphicalview,
            value: DisplayState.Graphical,
            icon: <CalendarOutlined alt="Graphical" />,
          },
        ]}
      />
      {isValidDate(dateInterval.min.unix) &&
        isValidDate(dateInterval.max.unix) && (
          <Typography.Text>
            {RangeReduce.daysRangePresent(dateRangeDays, false)}
          </Typography.Text>
        )}
    </div>
  );
}
